import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Lock, RefreshCw, Save, Inbox, FileText, History, PlusCircle } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/mdh/Nav";
import { API, BACKEND } from "@/lib/api";


export default function Admin() {
    const [key, setKey] = useState("");
    const [unlocked, setUnlocked] = useState(false);
    const [requests, setRequests] = useState([]);
    const [sections, setSections] = useState([]);
    const [entries, setEntries] = useState([]);
    const [tab, setTab] = useState("requests");
    const [selected, setSelected] = useState(null);
    const [draftTitle, setDraftTitle] = useState("");
    const [draft, setDraft] = useState("");
    const [cSelected, setCSelected] = useState(null);
    const [cForm, setCForm] = useState({ version: "", title: "", tag: "New", date: "", source: "" });
    const [notifMode, setNotifMode] = useState("instant");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const headers = (k = key) => ({ "X-Admin-Key": k });

    const loadAll = async (k) => {
        const r = await fetch(`${API}/admin/access-requests`, { headers: headers(k) });
        if (r.status === 401) throw new Error("Invalid passkey");
        if (!r.ok) throw new Error("Request failed");
        const data = await r.json();
        setRequests(data.requests || []);
        const s = await fetch(`${API}/docs-sections`).then((res) => res.json());
        setSections(s.sections || []);
        const c = await fetch(`${API}/changelog`).then((res) => res.json());
        setEntries(c.entries || []);
        const ns = await fetch(`${API}/admin/settings`, { headers: headers(k) }).then((res) =>
            res.json(),
        );
        setNotifMode(ns.mode || "instant");
    };

    const unlock = async () => {
        setBusy(true);
        setError("");
        try {
            await loadAll(key);
            setUnlocked(true);
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    const refresh = async () => {
        setBusy(true);
        try {
            await loadAll(key);
            toast.success("Refreshed");
        } catch (e) {
            toast.error(e.message);
        } finally {
            setBusy(false);
        }
    };

    const exportCsv = () => {
        const rows = [
            ["email", "source", "created_at"],
            ...requests.map((r) => [r.email, r.source, r.created_at]),
        ];
        const csv = rows
            .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
            .join("\n");
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = "mdh-access-requests.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const pick = (s) => {
        setSelected(s.id);
        setDraftTitle(s.title);
        setDraft(s.source);
    };

    const saveSection = async () => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/docs-sections/${selected}`, {
                method: "PUT",
                headers: { ...headers(), "Content-Type": "application/json" },
                body: JSON.stringify({ title: draftTitle, source: draft }),
            });
            if (r.status === 401) throw new Error("Invalid passkey");
            if (!r.ok) throw new Error("Save failed");
            setSections((prev) =>
                prev.map((s) => (s.id === selected ? { ...s, title: draftTitle, source: draft } : s)),
            );
            toast.success("Docs section updated", {
                description: "Live on /docs immediately — no redeploy.",
            });
        } catch (e) {
            toast.error(e.message);
        } finally {
            setBusy(false);
        }
    };

    const pickEntry = (e) => {
        setCSelected(e.id);
        setCForm({ version: e.version, title: e.title, tag: e.tag, date: e.date, source: e.source });
    };

    const newEntry = () => {
        setCSelected("new");
        setCForm({
            version: "",
            title: "",
            tag: "New",
            date: new Date().toISOString().slice(0, 10),
            source: "",
        });
    };

    const saveEntry = async () => {
        setBusy(true);
        try {
            const isNew = cSelected === "new";
            const r = await fetch(
                isNew ? `${API}/admin/changelog` : `${API}/admin/changelog/${cSelected}`,
                {
                    method: isNew ? "POST" : "PUT",
                    headers: { ...headers(), "Content-Type": "application/json" },
                    body: JSON.stringify(cForm),
                },
            );
            if (r.status === 401) throw new Error("Invalid passkey");
            if (r.status === 409) throw new Error("That version already exists");
            if (!r.ok) throw new Error("Save failed");
            const c = await fetch(`${API}/changelog`).then((res) => res.json());
            setEntries(c.entries || []);
            setCSelected(cForm.version.toLowerCase().replace(/\s+/g, ""));
            toast.success(isNew ? "Changelog entry published" : "Changelog entry updated", {
                description: "Live on /changelog immediately.",
            });
        } catch (e) {
            toast.error(e.message);
        } finally {
            setBusy(false);
        }
    };

    const saveNotifMode = async (mode) => {
        setNotifMode(mode);
        try {
            const r = await fetch(`${API}/admin/settings`, {
                method: "PUT",
                headers: { ...headers(), "Content-Type": "application/json" },
                body: JSON.stringify({ mode }),
            });
            if (r.status === 401) throw new Error("Invalid passkey");
            if (!r.ok) throw new Error("Save failed");
            toast.success(mode === "digest" ? "Daily digest enabled" : "Instant alerts enabled", {
                description:
                    mode === "digest"
                        ? "At most one email per day, summarizing every new request."
                        : "Every new request emails you right away.",
            });
        } catch (e) {
            toast.error(e.message);
        }
    };

    if (!unlocked) {
        return (
            <div className="flex min-h-screen items-center justify-center px-5">
                <div
                    data-testid="admin-gate"
                    className="glass-panel w-full max-w-sm rounded-xl p-8"
                >
                    <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-md border border-cyan-500/30 bg-cyan-950/50">
                            <Lock className="h-4 w-4 text-cyan-300" />
                        </span>
                        <div>
                            <h1 className="font-display text-lg font-bold text-slate-100">
                                Admin access
                            </h1>
                            <p className="font-mono text-[10px] text-slate-500">
                                Requests · Docs content
                            </p>
                        </div>
                    </div>
                    <input
                        data-testid="admin-passkey-input"
                        type="password"
                        value={key}
                        onChange={(e) => setKey(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && unlock()}
                        placeholder="Admin passkey"
                        autoComplete="current-password"
                        className="mt-6 w-full rounded-md border border-slate-800 bg-slate-950/70 px-4 py-2.5 font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none focus:ring-1 focus:ring-cyan-500/40"
                    />
                    {error && (
                        <p data-testid="admin-error" className="mt-2 font-mono text-[11px] text-rose-400">
                            {error}
                        </p>
                    )}
                    <button
                        data-testid="admin-unlock-button"
                        onClick={unlock}
                        disabled={busy || key.length < 8}
                        className="mt-4 w-full rounded-md bg-cyan-400 px-4 py-2.5 text-xs font-bold text-slate-950 transition-colors hover:bg-cyan-300 disabled:opacity-50"
                    >
                        {busy ? "Checking…" : "Unlock"}
                    </button>
                    <Link
                        to="/"
                        data-testid="admin-back-link"
                        className="mt-4 flex items-center justify-center gap-1.5 font-mono text-[11px] text-slate-500 hover:text-cyan-300"
                    >
                        <ArrowLeft className="h-3 w-3" /> Back to site
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="admin-dashboard" className="min-h-screen">
            <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-800/80 bg-[#0B0F17]/92">
                <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 lg:px-8">
                    <div className="flex items-center gap-3">
                        <Logo />
                        <span className="rounded border border-amber-500/30 bg-amber-950/50 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-amber-300">
                            Admin
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="admin-refresh-button"
                            onClick={refresh}
                            className="flex items-center gap-1.5 rounded-md border border-slate-800 px-3 py-2 text-xs text-slate-400 transition-colors hover:border-cyan-500/40 hover:text-cyan-300"
                        >
                            <RefreshCw className="h-3.5 w-3.5" /> Refresh
                        </button>
                        <Link
                            to="/"
                            data-testid="admin-home-link"
                            className="flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold text-slate-300 hover:text-cyan-300"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" /> Back to site
                        </Link>
                    </div>
                </div>
            </header>

            <div className="mx-auto max-w-6xl px-5 pb-24 pt-28 lg:px-8">
                <div className="flex items-center gap-1 rounded-lg border border-slate-800 bg-slate-950/60 p-1 w-fit">
                    {[
                        { id: "requests", label: "Access requests", Icon: Inbox },
                        { id: "docs", label: "Docs content", Icon: FileText },
                        { id: "changelog", label: "Changelog", Icon: History },
                    ].map(({ id, label, Icon }) => (
                        <button
                            key={id}
                            data-testid={`admin-tab-${id}`}
                            onClick={() => setTab(id)}
                            className={`flex items-center gap-2 rounded-md px-4 py-2 font-mono text-xs transition-colors ${
                                tab === id
                                    ? "bg-cyan-950/70 text-cyan-300"
                                    : "text-slate-500 hover:text-slate-300"
                            }`}
                        >
                            <Icon className="h-3.5 w-3.5" />
                            {label}
                        </button>
                    ))}
                </div>

                {tab === "requests" && (
                    <div className="mt-8">
                        <div className="flex items-center justify-between">
                            <h1 className="font-display text-xl font-bold text-slate-100">
                                Access requests{" "}
                                <span className="ml-1 font-mono text-sm text-slate-500">
                                    ({requests.length})
                                </span>
                            </h1>
                            <button
                                data-testid="admin-export-csv-button"
                                onClick={exportCsv}
                                disabled={requests.length === 0}
                                className="flex items-center gap-1.5 rounded-md border border-cyan-500/40 bg-cyan-950/40 px-4 py-2 font-mono text-xs font-semibold text-cyan-300 transition-colors hover:bg-cyan-950/70 disabled:opacity-50"
                            >
                                <Download className="h-3.5 w-3.5" /> Export CSV
                            </button>
                        </div>
                        <div className="glass-panel mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl px-5 py-4">
                            <div>
                                <div className="font-mono text-xs font-semibold text-slate-200">
                                    Email notifications
                                </div>
                                <div className="mt-0.5 max-w-md font-mono text-[10px] leading-relaxed text-slate-500">
                                    Instant emails you per request. Daily digest sends at most one
                                    email per day, summarizing every new request since the last one.
                                </div>
                            </div>
                            <div className="flex items-center gap-1 rounded-md border border-slate-800 bg-slate-950/60 p-0.5">
                                {[
                                    ["instant", "Instant"],
                                    ["digest", "Daily digest"],
                                ].map(([m, label]) => (
                                    <button
                                        key={m}
                                        data-testid={`admin-notif-${m}`}
                                        onClick={() => saveNotifMode(m)}
                                        className={`rounded px-3 py-1.5 font-mono text-[11px] transition-colors ${
                                            notifMode === m
                                                ? "bg-cyan-950/70 text-cyan-300"
                                                : "text-slate-500 hover:text-slate-300"
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="glass-panel mt-5 overflow-hidden rounded-xl">
                            <table className="w-full text-left font-mono text-xs">
                                <thead>
                                    <tr className="border-b border-slate-800 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                        <th className="px-4 py-3">Email</th>
                                        <th className="px-4 py-3">Source</th>
                                        <th className="px-4 py-3">Received (UTC)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {requests.length === 0 && (
                                        <tr>
                                            <td colSpan={3} className="px-4 py-10 text-center text-slate-500">
                                                No requests yet — share the site and check back.
                                            </td>
                                        </tr>
                                    )}
                                    {requests.map((r) => (
                                        <tr
                                            key={r.id}
                                            data-testid="admin-request-row"
                                            className="border-b border-slate-800/60 last:border-0 hover:bg-slate-900/40"
                                        >
                                            <td className="px-4 py-3 text-cyan-300">{r.email}</td>
                                            <td className="px-4 py-3 text-slate-400">{r.source}</td>
                                            <td className="px-4 py-3 text-slate-500">{r.created_at}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {tab === "docs" && (
                    <div className="mt-8 grid gap-6 lg:grid-cols-[240px_1fr]">
                        <div className="space-y-1">
                            {sections.map((s) => (
                                <button
                                    key={s.id}
                                    data-testid={`admin-section-${s.id}`}
                                    onClick={() => pick(s)}
                                    className={`block w-full rounded-md px-3 py-2 text-left font-mono text-[11px] transition-colors ${
                                        selected === s.id
                                            ? "bg-cyan-950/60 text-cyan-300"
                                            : "text-slate-500 hover:bg-slate-900 hover:text-slate-300"
                                    }`}
                                >
                                    {s.title}
                                </button>
                            ))}
                        </div>
                        <div>
                            {selected ? (
                                <div className="glass-panel rounded-xl p-5">
                                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                        Section title
                                    </label>
                                    <input
                                        data-testid="admin-section-title-input"
                                        value={draftTitle}
                                        onChange={(e) => setDraftTitle(e.target.value)}
                                        className="mt-1.5 w-full rounded-md border border-slate-800 bg-slate-950/70 px-3.5 py-2 font-mono text-xs text-slate-200 focus:border-cyan-500/60 focus:outline-none"
                                    />
                                    <label className="mt-4 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                        Content (markdown-lite)
                                    </label>
                                    <textarea
                                        data-testid="admin-section-source-input"
                                        value={draft}
                                        onChange={(e) => setDraft(e.target.value)}
                                        rows={14}
                                        className="mt-1.5 w-full rounded-md border border-slate-800 bg-slate-950/70 p-3.5 font-mono text-[11px] leading-relaxed text-slate-200 focus:border-cyan-500/60 focus:outline-none"
                                    />
                                    <p className="mt-2 font-mono text-[10px] leading-relaxed text-slate-600">
                                        Blank line = new paragraph · ```label … ``` = code block ·
                                        &gt;&gt; METHOD /path :: text = endpoint card · **bold** ·
                                        `code`
                                    </p>
                                    <button
                                        data-testid="admin-section-save-button"
                                        onClick={saveSection}
                                        disabled={busy}
                                        className="mt-4 flex items-center gap-1.5 rounded-md bg-cyan-400 px-4 py-2.5 text-xs font-bold text-slate-950 transition-colors hover:bg-cyan-300 disabled:opacity-50"
                                    >
                                        <Save className="h-3.5 w-3.5" />
                                        {busy ? "Saving…" : "Publish changes"}
                                    </button>
                                </div>
                            ) : (
                                <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-slate-800 font-mono text-xs text-slate-600">
                                    Select a section to edit
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {tab === "changelog" && (
                    <div className="mt-8 grid gap-6 lg:grid-cols-[240px_1fr]">
                        <div className="space-y-1">
                            <button
                                data-testid="admin-changelog-new"
                                onClick={newEntry}
                                className="flex w-full items-center gap-2 rounded-md border border-dashed border-cyan-500/40 px-3 py-2 font-mono text-[11px] text-cyan-300 transition-colors hover:bg-cyan-950/30"
                            >
                                <PlusCircle className="h-3.5 w-3.5" /> New entry
                            </button>
                            {entries.map((e) => (
                                <button
                                    key={e.id}
                                    data-testid={`admin-changelog-${e.id}`}
                                    onClick={() => pickEntry(e)}
                                    className={`block w-full rounded-md px-3 py-2 text-left font-mono text-[11px] transition-colors ${
                                        cSelected === e.id
                                            ? "bg-cyan-950/60 text-cyan-300"
                                            : "text-slate-500 hover:bg-slate-900 hover:text-slate-300"
                                    }`}
                                >
                                    {e.version} — {e.title}
                                </button>
                            ))}
                        </div>
                        <div>
                            {cSelected !== null ? (
                                <div className="glass-panel rounded-xl p-5">
                                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-[110px_1fr_130px_140px]">
                                        <div>
                                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                                Version
                                            </label>
                                            <input
                                                data-testid="admin-changelog-version-input"
                                                value={cForm.version}
                                                disabled={cSelected !== "new"}
                                                onChange={(e) => setCForm({ ...cForm, version: e.target.value })}
                                                placeholder="v2.5"
                                                className="mt-1.5 w-full rounded-md border border-slate-800 bg-slate-950/70 px-3 py-2 font-mono text-xs text-slate-200 focus:border-cyan-500/60 focus:outline-none disabled:opacity-50"
                                            />
                                        </div>
                                        <div>
                                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                                Title
                                            </label>
                                            <input
                                                data-testid="admin-changelog-title-input"
                                                value={cForm.title}
                                                onChange={(e) => setCForm({ ...cForm, title: e.target.value })}
                                                placeholder="What shipped"
                                                className="mt-1.5 w-full rounded-md border border-slate-800 bg-slate-950/70 px-3 py-2 font-mono text-xs text-slate-200 focus:border-cyan-500/60 focus:outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                                Tag
                                            </label>
                                            <select
                                                data-testid="admin-changelog-tag-select"
                                                value={cForm.tag}
                                                onChange={(e) => setCForm({ ...cForm, tag: e.target.value })}
                                                className="mt-1.5 w-full rounded-md border border-slate-800 bg-slate-950/70 px-2.5 py-2 font-mono text-xs text-slate-200 focus:border-cyan-500/60 focus:outline-none"
                                            >
                                                {["New", "Improvement", "Fix", "Security"].map((t) => (
                                                    <option key={t}>{t}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                                Date
                                            </label>
                                            <input
                                                data-testid="admin-changelog-date-input"
                                                value={cForm.date}
                                                onChange={(e) => setCForm({ ...cForm, date: e.target.value })}
                                                className="mt-1.5 w-full rounded-md border border-slate-800 bg-slate-950/70 px-3 py-2 font-mono text-xs text-slate-200 focus:border-cyan-500/60 focus:outline-none"
                                            />
                                        </div>
                                    </div>
                                    <label className="mt-4 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                        Content (markdown-lite)
                                    </label>
                                    <textarea
                                        data-testid="admin-changelog-source-input"
                                        value={cForm.source}
                                        onChange={(e) => setCForm({ ...cForm, source: e.target.value })}
                                        rows={10}
                                        className="mt-1.5 w-full rounded-md border border-slate-800 bg-slate-950/70 p-3.5 font-mono text-[11px] leading-relaxed text-slate-200 focus:border-cyan-500/60 focus:outline-none"
                                    />
                                    <button
                                        data-testid="admin-changelog-save-button"
                                        onClick={saveEntry}
                                        disabled={busy || !cForm.version.trim() || !cForm.title.trim() || !cForm.source.trim()}
                                        className="mt-4 flex items-center gap-1.5 rounded-md bg-cyan-400 px-4 py-2.5 text-xs font-bold text-slate-950 transition-colors hover:bg-cyan-300 disabled:opacity-50"
                                    >
                                        <Save className="h-3.5 w-3.5" />
                                        {busy ? "Publishing…" : cSelected === "new" ? "Publish entry" : "Save changes"}
                                    </button>
                                </div>
                            ) : (
                                <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-slate-800 font-mono text-xs text-slate-600">
                                    Select an entry or create a new one
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
